import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useBranding } from '../branding';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, Check, RefreshCw, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useSEO } from '../hooks/useSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
  'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const RADIUS_OPTIONS = ['Local (under 50 miles)', 'Intermediate (50-200 miles)', 'Long Haul (200+ miles)'];
const OPS = ['Authority For Hire', 'Private Carrier', 'Private Passenger', 'Lease/Owner Operator'];
const COVERAGES = [
  { key: 'apd', label: 'Auto Physical Damage (APD)' },
  { key: 'mtc', label: 'Motor Truck Cargo (MTC)' },
  { key: 'ti', label: 'Trailer Interchange (TI)' },
  { key: 'ntl', label: 'Non-Trucking Liability (NTL)' },
  { key: 'tgl', label: "Trucker's General Liability (TGL)" },
];

const initialState = {
  organization_type: 'Company',
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  garaging_address: '',
  garaging_city: '',
  garaging_state: '',
  garaging_zip: '',
  dot_number: '',
  mc_number: '',
  years_in_business: 1,
  number_of_vehicles: 1,
  vehicle_types: 'Tractor',
  operation_classification: 'Authority For Hire',
  radius: 'Local (under 50 miles)',
  effective_date: '',
  current_insurer: '',
  prior_claims: '',
  additional_notes: '',
  coverage_keys: ['apd', 'mtc'],
  drivers: [],
  vehicles: [],
};

/**
 * <PublicQuoteRequestPage> — public, no-auth landing form for prospective
 * insureds. Posts to /api/public/insured-requests with a captcha + a
 * honeypot. Successful submit yields a reference id ("INS-2026-0042").
 *
 * Captcha mode is decided by the server via /api/public/captcha/config:
 *   - "turnstile" → renders the embedded Cloudflare Turnstile widget.
 *   - "math"      → falls back to the legacy in-house math captcha.
 */
const PublicQuoteRequestPage = () => {
  // Session 215.84 — GSC targeting for "trucking insurance quote"
  // and "wholesale trucking insurance quote" queries.
  useSEO({
    title:
      'Get a Trucking Insurance Quote | Wholesale Transportation Insurance — Zion Underwriting Partners',
    description:
      'Request a wholesale trucking insurance and transportation insurance quote from Zion Underwriting Partners. Multi-carrier commercial trucking insurance rating for Auto Physical Damage, Motor Truck Cargo, Non-Trucking Liability, and Trucker\'s General Liability.',
    path: '/get-a-quote',
  });
  const branding = useBranding();
  // Session 213.99 — When loaded with `?member=1`, the page is the
  // NTOA-member-portal Insurance tab in disguise: same form UI, but the
  // submit posts to the authenticated /insured/insurance-request endpoint
  // (no captcha; the session cookie authenticates). Captcha config / widget
  // load is skipped to keep the experience seamless for logged-in members.
  const memberMode = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('member') === '1';
  const [form, setForm] = useState(initialState);
  const [captchaMode, setCaptchaMode] = useState(null); // null until config loads
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);

  const refreshCaptcha = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/public/captcha`);
      setCaptchaToken(r.data?.token || '');
      setCaptchaQuestion(r.data?.question || '');
    } catch {
      toast.error('Could not load captcha — please refresh the page.');
    }
  }, []);

  // Load captcha config so we know which widget to render
  useEffect(() => {
    if (memberMode) return undefined;  // Session 213.99 — skip captcha for authenticated members
    // Session 214.5 — keep the direct submission form noindex'd. The
    // public CTA now funnels through /quote → GetAppointedGate.
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/public/captcha/config`);
        if (cancelled) return;
        setCaptchaMode(r.data?.mode || 'math');
        // Session 213.63 — prefer the dedicated quote widget site key from
        // env (REACT_APP_TURNSTILE_SITE_QUOTE) so the public quote form
        // gets its own analytics. Falls back to whatever the backend
        // advertises (legacy single-widget setups).
        const envKey = process.env.REACT_APP_TURNSTILE_SITE_QUOTE;
        setTurnstileSiteKey(envKey || r.data?.site_key || '');
        if ((r.data?.mode || 'math') === 'math') {
          refreshCaptcha();
        }
      } catch {
        // Default to math captcha if config is unreachable
        setCaptchaMode('math');
        refreshCaptcha();
      }
    })();
    return () => { cancelled = true; try { document.head.removeChild(meta); } catch (_) { /* noop */ } };
  }, [refreshCaptcha, memberMode]);

  // Inject the Turnstile script + render the widget when the mode says so.
  useEffect(() => {
    if (captchaMode !== 'turnstile' || !turnstileSiteKey) return undefined;
    const SCRIPT_ID = 'cf-turnstile-script';

    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainerRef.current) return;
      // Tear down any previous widget on re-renders
      if (turnstileWidgetIdRef.current) {
        try { window.turnstile.remove(turnstileWidgetIdRef.current); } catch (_) { /* noop */ }
        turnstileWidgetIdRef.current = null;
      }
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: turnstileSiteKey,
        theme: 'light',
        callback: (token) => setCaptchaToken(token || ''),
        'error-callback': () => setCaptchaToken(''),
        'expired-callback': () => setCaptchaToken(''),
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true;
      s.defer = true;
      s.onload = renderWidget;
      document.head.appendChild(s);
    } else {
      // Script tag exists but window.turnstile not yet ready — poll briefly
      const t = setInterval(() => {
        if (window.turnstile) { clearInterval(t); renderWidget(); }
      }, 100);
      return () => clearInterval(t);
    }
    return () => {
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetIdRef.current); } catch (_) { /* noop */ }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [captchaMode, turnstileSiteKey]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleCoverage = (k) => {
    setForm((p) => ({
      ...p,
      coverage_keys: p.coverage_keys.includes(k)
        ? p.coverage_keys.filter((x) => x !== k)
        : [...p.coverage_keys, k],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.coverage_keys.length === 0) {
      toast.error('Pick at least one coverage');
      return;
    }
    if (!memberMode) {
      if (captchaMode === 'turnstile') {
        if (!captchaToken) {
          toast.error('Complete the captcha challenge');
          return;
        }
      } else if (!captchaAnswer) {
        toast.error('Answer the captcha');
        return;
      }
    }
    setSubmitting(true);
    try {
      const coverage_types = COVERAGES.reduce(
        (acc, c) => ({ ...acc, [c.key]: form.coverage_keys.includes(c.key) }),
        {},
      );
      const payload = {
        organization_type: form.organization_type,
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        garaging_address: form.garaging_address,
        garaging_city: form.garaging_city,
        garaging_state: form.garaging_state,
        garaging_zip: form.garaging_zip,
        mailing_address_same_as_garaging: true,
        dot_number: form.dot_number,
        mc_number: form.mc_number || null,
        years_in_business: Number(form.years_in_business) || 1,
        number_of_vehicles: Number(form.number_of_vehicles) || 1,
        drivers: [],
        vehicles: [],
        coverage_types,
        tgl_selected: form.coverage_keys.includes('tgl'),
        effective_date: form.effective_date,
        vehicle_types: form.vehicle_types,
        operation_classification: form.operation_classification,
        radius: form.radius,
        current_insurer: form.current_insurer || null,
        prior_claims: form.prior_claims || null,
        additional_notes: form.additional_notes || null,
        captcha_token: captchaToken,
        captcha_answer: captchaAnswer,
        website, // honeypot
      };
      // Session 213.99 — Authenticated members skip captcha + honeypot and
      // post to the member-portal endpoint so the request lands in the
      // public_quote_requests queue with source='ntoa_member_portal'.
      let r;
      if (memberMode) {
        const { captcha_token, captcha_answer, website: _hp, ...memberPayload } = payload;
        r = await axios.post(
          `${API_URL}/api/insured/insurance-request`,
          memberPayload,
          { withCredentials: true },
        );
      } else {
        r = await axios.post(`${API_URL}/api/public/insured-requests`, payload);
      }
      setSubmitted(r.data?.reference_id || 'submitted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed. Please try again.');
      // Reset captcha on any failure so a stale token doesn't block retries
      if (captchaMode === 'turnstile') {
        setCaptchaToken('');
        if (window.turnstile && turnstileWidgetIdRef.current) {
          try { window.turnstile.reset(turnstileWidgetIdRef.current); } catch (_) { /* noop */ }
        }
      } else {
        refreshCaptcha();
        setCaptchaAnswer('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full" data-testid="public-quote-success">
          <CardContent className="pt-8 pb-6 text-center space-y-3">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="h-6 w-6 text-emerald-700" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Request received</h2>
            <p className="text-sm text-gray-600">
              Your reference number is <span className="font-mono font-semibold">{submitted}</span>.
              An underwriter will route your request to one or more agencies who specialize in your
              line of business. Expect a follow-up within one business day.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-blue-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f]">
            {branding?.name || 'Get a Trucking Insurance Quote'}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Tell us about your operation and we&apos;ll route your request to the right agency.
            Takes about 3 minutes.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="public-quote-form">
          <Card>
            <CardHeader>
              <CardTitle>Your Business</CardTitle>
              <CardDescription>How do we reach you?</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Company Name *</Label>
                <Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} required data-testid="pq-company" />
              </div>
              <div>
                <Label>Contact Name *</Label>
                <Input value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} required data-testid="pq-contact" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required data-testid="pq-email" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} required data-testid="pq-phone" />
              </div>
              <div>
                <Label>Years in Business *</Label>
                <Input type="number" min="0" value={form.years_in_business} onChange={(e) => set('years_in_business', e.target.value)} required data-testid="pq-yib" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Garaging Address</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Street Address *</Label>
                <Input value={form.garaging_address} onChange={(e) => set('garaging_address', e.target.value)} required data-testid="pq-addr" />
              </div>
              <div>
                <Label>City *</Label>
                <Input value={form.garaging_city} onChange={(e) => set('garaging_city', e.target.value)} required data-testid="pq-city" />
              </div>
              <div>
                <Label>State *</Label>
                <Select value={form.garaging_state} onValueChange={(v) => set('garaging_state', v)}>
                  <SelectTrigger data-testid="pq-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    {STATES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>ZIP *</Label>
                <Input value={form.garaging_zip} onChange={(e) => set('garaging_zip', e.target.value)} required data-testid="pq-zip" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Operations</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>USDOT Number *</Label>
                <Input value={form.dot_number} onChange={(e) => set('dot_number', e.target.value.replace(/\D/g, ''))} required data-testid="pq-dot" />
              </div>
              <div>
                <Label>MC Number</Label>
                <Input value={form.mc_number} onChange={(e) => set('mc_number', e.target.value.replace(/\D/g, ''))} data-testid="pq-mc" />
              </div>
              <div>
                <Label>Number of Power Units *</Label>
                <Input type="number" min="1" value={form.number_of_vehicles} onChange={(e) => set('number_of_vehicles', e.target.value)} required data-testid="pq-units" />
              </div>
              <div>
                <Label>Operation Type *</Label>
                <Select value={form.operation_classification} onValueChange={(v) => set('operation_classification', v)}>
                  <SelectTrigger data-testid="pq-op"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Radius *</Label>
                <Select value={form.radius} onValueChange={(v) => set('radius', v)}>
                  <SelectTrigger data-testid="pq-radius"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RADIUS_OPTIONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle Type *</Label>
                <Input value={form.vehicle_types} onChange={(e) => set('vehicle_types', e.target.value)} placeholder="Tractor, Box truck, Reefer…" required data-testid="pq-vtype" />
              </div>
              <div>
                <Label>Desired Effective Date *</Label>
                <Input type="date" value={form.effective_date} onChange={(e) => set('effective_date', e.target.value)} required data-testid="pq-effdate" />
              </div>
              <div>
                <Label>Current Insurer</Label>
                <Input value={form.current_insurer} onChange={(e) => set('current_insurer', e.target.value)} data-testid="pq-current-insurer" />
              </div>
              <div className="sm:col-span-2">
                <Label>Prior Claims (last 3 yrs)</Label>
                <Textarea rows={2} value={form.prior_claims} onChange={(e) => set('prior_claims', e.target.value)} data-testid="pq-claims" />
              </div>
              <div className="sm:col-span-2">
                <Label>Additional Notes</Label>
                <Textarea rows={3} value={form.additional_notes} onChange={(e) => set('additional_notes', e.target.value)} data-testid="pq-notes" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Coverage Interest</CardTitle>
              <CardDescription>Pick everything you&apos;d like quoted (an agent can adjust later).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {COVERAGES.map((c) => (
                <label
                  key={c.key}
                  className="flex items-center gap-2 border rounded p-2 cursor-pointer hover:bg-gray-50"
                >
                  <Checkbox
                    checked={form.coverage_keys.includes(c.key)}
                    onCheckedChange={() => toggleCoverage(c.key)}
                    data-testid={`pq-cov-${c.key}`}
                  />
                  <span className="text-sm">{c.label}</span>
                </label>
              ))}
            </CardContent>
          </Card>

          {/* Honeypot — hidden from real users */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
            aria-hidden="true"
          />

          {!memberMode && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2 text-blue-900 font-medium">
                <Shield className="h-4 w-4" /> Quick anti-spam check
              </div>
              {captchaMode === 'turnstile' ? (
                <div ref={turnstileContainerRef} data-testid="pq-turnstile" />
              ) : captchaMode === 'math' ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-sm font-mono bg-white px-3 py-2 border rounded" data-testid="pq-captcha-question">
                    {captchaQuestion || 'Loading…'}
                  </div>
                  <Input
                    className="max-w-[120px]"
                    placeholder="Answer"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    data-testid="pq-captcha-answer"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { refreshCaptcha(); setCaptchaAnswer(''); }}
                    data-testid="pq-captcha-refresh"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-slate-500">Loading…</div>
              )}
            </CardContent>
          </Card>
          )}

          {memberMode && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              You're signed in as an NTOA member — this request will be routed to our team and assigned to an agency partner. No captcha needed.
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#1e3a5f] hover:bg-[#162a45] text-white py-6 text-base font-semibold"
            data-testid="pq-submit-btn"
          >
            {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>) : 'Submit Quote Request'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PublicQuoteRequestPage;
